import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Container, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Navbar, CustomButton, LoadingSpinner } from '../components';
import { authAPI, avatarsAPI } from '../services/api';
import { extractGoogleId, extractGoogleUserInfo } from '../utils/googleAuth';
import { getPostAuthRoute, resolvePostAuthRoute, userHasAvatars, shouldRouteInviteeToChat, shouldRouteUnpaidSendInviteToPayment, syncSignupInviteParamsFromMe, resolveInviteAvatarIdForRelation, extractAvatarsList, resolveInviteePostAuthRoute, clearStaleAuthSessionCaches } from '../utils/freeTrial';
import './SigninPage.css';

const SigninPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const googleInitialized = useRef(false);
  const errorShownRef = useRef(false);
  const userFetchedRef = useRef(false); // Prevent duplicate getCurrentUser calls
  const isFetchingUserRef = useRef(false); // Track if currently fetching user

  // Detect an in-progress create-avatar flow (draft/payment context) so we don't bounce users back to /create-avatar
  // after they re-login while avatar creation is still pending.
  const hasPendingCreateAvatarFlow = () => {
    try {
      // 1) Per-user draft saved by CreateAvatarPage: create_avatar_payment_draft_<uid>
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const k = sessionStorage.key(i);
        if (!k) continue;
        if (k.startsWith('create_avatar_payment_draft_')) return true;
        if (k.startsWith('payment_processing_')) return true;
      }

      // 2) PayPal context used during redirects/popups
      const checkoutCtx =
        sessionStorage.getItem('paypal_checkout_context') ||
        localStorage.getItem('paypal_checkout_context');
      if (checkoutCtx) return true;

      // 3) Capture result (used to activate a plan right after payment)
      const capture = sessionStorage.getItem('paypal_capture_result');
      if (capture) return true;
    } catch (_) {
      // ignore
    }
    return false;
  };

  // Get invite params from URL (invitation links can target /signin for existing users).
  const inviteId = searchParams.get('invite_id');
  const inviteEmail = searchParams.get('invite_email');
  const inviteAvatarId = searchParams.get('avatar_id');

  // Google OAuth Client ID - should be set in environment variables
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      // Prefill invited email to reduce mismatch errors
      email: inviteEmail ? decodeURIComponent(inviteEmail) : '',
      password: ''
    }
  });

  // NOTE: Do NOT auto-redirect invite links from /signin -> /signup.
  // Existing users must be able to sign in while preserving invite_id so backend can mark Invitee.invite_accepted.

  // Clear "session expired" toast when landing on sign-in (e.g. after 401 redirect) so the page isn't cluttered
  useEffect(() => {
    toast.dismiss('session-expired');
  }, []);

  // Helper function to fetch user data once after signin - prevents duplicate API calls
  const fetchUserDataAfterSignin = async () => {
    // Check if already fetched or currently fetching
    if (userFetchedRef.current || isFetchingUserRef.current) {
      console.log('SigninPage: User data already fetched or currently fetching, skipping API call');
      const storedUserId = sessionStorage.getItem('user_id');
      if (storedUserId) {
        let cachedAvatars = [];
        try {
          cachedAvatars = JSON.parse(sessionStorage.getItem('avatars_data') || '[]');
        } catch (_) {
          cachedAvatars = [];
        }
        let me = null;
        try {
          me = JSON.parse(sessionStorage.getItem('user_data') || 'null');
        } catch (_) {
          me = null;
        }
        return {
          userId: parseInt(storedUserId, 10),
          avatars: Array.isArray(cachedAvatars) ? cachedAvatars : [],
          me,
        };
      }
      return null;
    }

    try {
      // Set flags immediately to prevent duplicate calls (synchronous)
      isFetchingUserRef.current = true;
      userFetchedRef.current = true;
      sessionStorage.setItem('user_fetched', 'true'); // Mark in session immediately
      console.log('SigninPage: Fetching user data from API (first time only)...');

      const userResponse = await authAPI.getCurrentUser();
      const meOk =
        (userResponse.status === true || userResponse.status_code === 200) &&
        userResponse.data;
      if (meOk) {
        const userData = userResponse.data;
        syncSignupInviteParamsFromMe(userData, inviteAvatarId);
        const userId = userData?.id || userData?.user_id;

        if (userId) {
          // Store full user data in sessionStorage for Navbar and other components
          try {
            sessionStorage.setItem('user_data', JSON.stringify(userData));
          } catch (error) {
            console.warn('Could not store user data in sessionStorage:', error);
          }

          // Cache userId in sessionStorage
          sessionStorage.setItem('user_id', userId.toString());
          console.log('SigninPage: User data fetched and cached. UserId:', userId);

          // my-details includes shared avatars (family/gift invitees), not only owned ones
          const avatarsResponse = await avatarsAPI.getMyDetails();
          const avatars = extractAvatarsList(avatarsResponse);

          // Store avatars data in sessionStorage
          try {
            sessionStorage.setItem('avatars_data', JSON.stringify(avatars));
          } catch (error) {
            console.warn('Could not store avatars data in sessionStorage:', error);
          }

          // Cache avatars info in sessionStorage
          if (avatars.length > 0) {
            sessionStorage.setItem('has_avatars', 'true');
            sessionStorage.setItem('avatars_fetched', 'true');
          } else {
            sessionStorage.setItem('has_avatars', 'false');
            sessionStorage.setItem('avatars_fetched', 'true');
          }

          // Return avatar info for routing
          return { userId, avatars, me: userData };
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Reset refs on error so it can retry if needed (but not on auth errors)
      if (error?.status !== 401) {
        userFetchedRef.current = false;
        sessionStorage.removeItem('user_fetched');
        sessionStorage.removeItem('user_id');
      }
      throw error;
    } finally {
      isFetchingUserRef.current = false;
    }

    return null;
  };

  const resolvePostSigninRoute = async (fetched) => {
    const urlInviteId = inviteId || searchParams.get('invite_id');
    let storedInvite = {};
    try {
      storedInvite = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
    } catch (_) {
      storedInvite = {};
    }
    const iWillPay =
      storedInvite.i_will_pay ??
      searchParams.get('i_will_pay') ??
      fetched?.me?.invitee?.i_will_pay ??
      null;

    return resolvePostAuthRoute({
      avatars: fetched?.avatars || [],
      inviteId: urlInviteId || storedInvite.invite_id || fetched?.me?.invitee?.invite_id || null,
      iWillPay,
      userData: fetched?.me || null,
      token: localStorage.getItem('access_token'),
      fromInviteLink: Boolean(urlInviteId || storedInvite.invite_id || fetched?.me?.invitee?.invite_id),
    });
  };

  const navigateAfterSignin = async (fetched) => {
    const storedInviteParams = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
    const pendingAvatarId = resolveInviteAvatarIdForRelation({
      avatarId: inviteAvatarId,
      userData: fetched?.me,
      avatars: fetched?.avatars || [],
    });

    const isInvitee =
      shouldRouteInviteeToChat({
        inviteId: inviteId || storedInviteParams.invite_id,
        iWillPay: storedInviteParams.i_will_pay,
        userData: fetched?.me,
      }) ||
      Boolean(pendingAvatarId || storedInviteParams.invite_id);

    if (!isInvitee) {
      return false;
    }

    const inviteRoute = await resolveInviteePostAuthRoute({
      avatarId: pendingAvatarId,
      userId: fetched?.userId,
      avatars: fetched?.avatars || [],
      avatarsAPI,
      userData: fetched?.me,
      inviteId: inviteId || storedInviteParams.invite_id,
      iWillPay: storedInviteParams.i_will_pay,
    });

    if (inviteRoute) {
      if (inviteRoute.startsWith('/relation-capture') || inviteRoute === '/select-plan') {
        navigate(inviteRoute, { replace: true });
        return true;
      }
    }
    return false;
  };

  const finishSigninNavigation = async (fetched) => {
    const relationRedirected = await navigateAfterSignin(fetched);
    if (relationRedirected) return;

    // Existing avatar owners always go to chat.
    if (userHasAvatars(fetched?.avatars)) {
      navigate('/chat', { replace: true });
      return;
    }

    // Unpaid send-invite users must pay before chat.
    if (shouldRouteUnpaidSendInviteToPayment({ userData: fetched?.me })) {
      navigate('/select-plan', { replace: true });
      return;
    }

    // Invited users (gift or paid send-invite) — chat, not plan selection or create-avatar.
    if (shouldRouteInviteeToChat({ userData: fetched?.me })) {
      navigate('/chat', { replace: true });
      return;
    }

    const fromPath = location.state?.from;
    if (fromPath) {
      if (
        fromPath === '/create-avatar' &&
        ((fetched?.avatars && fetched.avatars.length > 0) || hasPendingCreateAvatarFlow())
      ) {
        navigate('/chat', { replace: true });
      } else {
        navigate(fromPath, { replace: true });
      }
      return;
    }

    if (hasPendingCreateAvatarFlow() && fetched?.avatars?.length > 0) {
      navigate('/chat');
      return;
    }

    navigate(await resolvePostSigninRoute(fetched));
  };

  // Handle Google credential response
  const handleGoogleCredential = useCallback(async (response) => {
    console.log('Google OAuth callback received for SignIn');

    if (!response?.credential) {
      toast.error('Failed to get Google credential');
      return;
    }

    setLoading(true);

    try {
      // Extract google_id (sub) from the ID token
      const googleId = extractGoogleId(response.credential);

      // Extract user info from the token
      const userInfo = extractGoogleUserInfo(response.credential);

      if (!userInfo.email) {
        throw new Error('Failed to decode Google credential or email not provided');
      }

      // First, try to sign in (login)
      try {
        const loginResponse = await authAPI.googleSignin({
          email: userInfo.email,
          google_id: googleId
        });

        // Handle login response structure:
        // Direct object: { access_token, refresh_token, token_type, user }
        // Or wrapped: { status, data: { access_token, refresh_token, user } }
        const accessToken = loginResponse.access_token || loginResponse.data?.access_token;
        const refreshToken = loginResponse.refresh_token || loginResponse.data?.refresh_token;
        const userData = loginResponse.user || loginResponse.data?.user;

        if (accessToken) {
          // Store tokens in localStorage
          localStorage.setItem('access_token', accessToken);
          if (refreshToken) {
            localStorage.setItem('refresh_token', refreshToken);
          }

          // Store user data if available in response
          if (userData) {
            try {
              sessionStorage.setItem('user_data', JSON.stringify(userData));
              if (userData.id) {
                sessionStorage.setItem('user_id', userData.id.toString());
              }
            } catch (error) {
              console.warn('Could not store user data in sessionStorage:', error);
            }
          }

          clearStaleAuthSessionCaches();
          userFetchedRef.current = false; // Reset ref for new signin
          // Clear chat-related localStorage (user-specific; prevents 403 when switching users)
          localStorage.removeItem('chat_session_id');
          localStorage.removeItem('chat_session_project_id');
          localStorage.removeItem('chat_messages');

          // Get user info to check for avatars (only called once)
          try {
            const fetched = await fetchUserDataAfterSignin();

            if (fetched && fetched.userId) {
              await finishSigninNavigation(fetched);
            } else {
              const fromPath = location.state?.from;
              navigate(fromPath || '/select-plan', { replace: true });
            }
          } catch (error) {
            console.error('Error checking avatars:', error);
            navigate('/chat');
          }
        } else {
          // If no access token, show error
          toast.error('Google login failed: No access token received from server.');
        }
      } catch (loginError) {
        // If login fails, try to register (sign up) the user
        console.log('Login failed, attempting registration:', loginError?.msg);
        toast.error("Email not found. Please register first.");
        navigate('/signup');
      }
    } catch (error) {
      console.error('Google authentication error:', error);
      const errorMessage = error.message || 'Google authentication failed. Please try again.';

      // Check if user already exists
      if (errorMessage.includes('already') || errorMessage.includes('exists')) {
        toast.error('Account already exists. Please sign in instead.', {
          toastId: 'google-user-exists',
        });
      } else {
        toast.error(errorMessage, {
          toastId: 'google-signin-error',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const response = await authAPI.login({
        email: data.email,
        password: data.password,
        flag: 'local',
        // Preserve invite_id through signin so backend can mark invite accepted (best-effort).
        ...(inviteId ? { invite_id: inviteId } : {})
      });

      // Handle login response structure:
      // Direct object: { access_token, refresh_token, token_type, user }
      // Or wrapped: { status, data: { access_token, refresh_token, user } }
      const accessToken = response.access_token || response.data?.access_token;
      const refreshToken = response.refresh_token || response.data?.refresh_token;
      const userData = response.user || response.data?.user;

      if (accessToken) {
        // Store tokens in localStorage
        localStorage.setItem('access_token', accessToken);
        if (refreshToken) {
          localStorage.setItem('refresh_token', refreshToken);
        }

        // Store user data if available in response
        if (userData) {
          try {
            sessionStorage.setItem('user_data', JSON.stringify(userData));
            if (userData.id) {
              sessionStorage.setItem('user_id', userData.id.toString());
            }
          } catch (error) {
            console.warn('Could not store user data in sessionStorage:', error);
          }
        }

        clearStaleAuthSessionCaches();
        userFetchedRef.current = false; // Reset ref for new signin
        // Clear chat-related localStorage (user-specific; prevents 403 when switching users)
        localStorage.removeItem('chat_session_id');
        localStorage.removeItem('chat_session_project_id');
        localStorage.removeItem('chat_messages');

        // Get user info to check for avatars (only called once); syncSignupInviteParamsFromMe runs inside fetchUserDataAfterSignin
        try {
          const userData = await fetchUserDataAfterSignin();

          if (userData && userData.userId) {
            await finishSigninNavigation(userData);
          } else {
            const fromPath = location.state?.from;
            navigate(fromPath || '/select-plan', { replace: true });
          }
        } catch (error) {
          console.error('Error checking avatars:', error);
          navigate('/chat');
        }
      } else {
        // If no access token, show error
        toast.error('Login failed: No access token received from server.');
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error details:', {
        status: error.status || error.statusCode,
        message: error.message,
        response: error.response
      });

      // Handle specific error types with user-friendly messages
      const errorStatus = error?.status || error?.statusCode;
      if (errorStatus === 429) {
        toast.error('Too many login attempts. Please wait a moment and try again.', {
          toastId: 'rate-limit-error',
          autoClose: 5000
        });
      } else if (errorStatus === 401) {
        toast.error('Invalid email or password. Please check your credentials and try again.', {
          toastId: 'auth-error',
          autoClose: 4000
        });

        // If this was an invite link and login failed, guide the user to signup (keeping invite params).
        if (inviteId || inviteEmail) {
          try {
            const params = new URLSearchParams();
            if (inviteId) params.set('invite_id', inviteId);
            if (inviteEmail) params.set('invite_email', inviteEmail);
            navigate(`/signup?${params.toString()}`);
          } catch (e) {
            // ignore navigation issues
          }
        }
      } else if (errorStatus === 422) {
        // Validation error - show the actual error message from API
        // Replace "string" with "Password" in error messages
        let errorMsg = error.message || 'Password validation failed. Please check your password and try again.';
        errorMsg = errorMsg.replace(/string/gi, 'Password');
        toast.error(errorMsg, {
          toastId: 'validation-error',
          autoClose: 5000
        });
      } else if (errorStatus === 400) {
        // Replace "string" with "Password" in error messages
        let errorMsg = error.message || 'Invalid request. Please check your password and try again.';
        errorMsg = errorMsg.replace(/string/gi, 'Password');
        toast.error(errorMsg, {
          toastId: 'bad-request-error',
          autoClose: 4000
        });
      } else {
        toast.error(error.message || 'Login failed. Please check your credentials and try again.', {
          toastId: 'login-error',
          autoClose: 4000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Initialize Google Sign-In when component mounts
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      console.warn('Google Client ID not configured. Set REACT_APP_GOOGLE_CLIENT_ID in .env');
      return;
    }

    const initializeGoogleSignIn = () => {
      if (typeof window !== 'undefined' && window.google && window.google.accounts && !googleInitialized.current) {
        try {
          // Initialize Google Identity Services for ID token (popup-based, doesn't require FedCM)
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleCredential,
            // Disable One Tap to avoid FedCM issues
            auto_select: false,
            // Use popup flow instead of FedCM
            use_fedcm_for_prompt: false,
          });

          googleInitialized.current = true;
        } catch (error) {
          console.error('Error initializing Google Sign-In:', error);
        }
      }
    };

    // Check if script already loaded
    const existingScript = document.getElementById('google-oauth-sdk');
    if (existingScript) {
      // Script already exists, just initialize
      if (typeof window !== 'undefined' && window.google) {
        initializeGoogleSignIn();
      } else {
        // Wait for script to be ready
        const checkInterval = setInterval(() => {
          if (typeof window !== 'undefined' && window.google) {
            initializeGoogleSignIn();
            clearInterval(checkInterval);
          }
        }, 100);

        setTimeout(() => clearInterval(checkInterval), 10000);
      }
      return;
    }

    // Load Google OAuth SDK script
    const script = document.createElement('script');
    script.id = 'google-oauth-sdk';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('Google OAuth SDK loaded for SignIn');
      initializeGoogleSignIn();
    };
    script.onerror = () => {
      console.error('Failed to load Google OAuth SDK');
    };
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Script cleanup is handled by React
    };
  }, [GOOGLE_CLIENT_ID, handleGoogleCredential]);

  const handleGoogleSignin = () => {
    // Prevent multiple error messages
    if (!GOOGLE_CLIENT_ID) {
      if (!errorShownRef.current) {
        console.error('Google Client ID not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your .env file');
        toast.error('Google Sign-In is not configured. Please contact support or use email signin.', {
          toastId: 'google-config-error', // Prevent duplicate toasts
        });
        errorShownRef.current = true;
        // Reset after 5 seconds to allow showing again if needed
        setTimeout(() => {
          errorShownRef.current = false;
        }, 5000);
      }
      return;
    }

    if (typeof window === 'undefined' || !window.google) {
      toast.info('Google Sign-In is loading. Please wait a moment and try again.', {
        toastId: 'google-loading',
      });
      return;
    }

    // Use a popup-based approach that doesn't rely on FedCM
    try {
      // Create a hidden button element and render Google button on it
      const buttonContainer = document.createElement('div');
      buttonContainer.style.position = 'absolute';
      buttonContainer.style.left = '-9999px';
      buttonContainer.style.opacity = '0';
      buttonContainer.style.pointerEvents = 'none';
      buttonContainer.style.width = '1px';
      buttonContainer.style.height = '1px';
      document.body.appendChild(buttonContainer);

      // Render Google button with explicit settings to avoid FedCM
      window.google.accounts.id.renderButton(buttonContainer, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        callback: handleGoogleCredential,
        // Explicitly disable FedCM
        use_fedcm_for_prompt: false,
      });

      // Find and click the button after it's rendered
      setTimeout(() => {
        const googleButton = buttonContainer.querySelector('div[role="button"]');
        if (googleButton) {
          // Use native click method
          googleButton.click();
        } else {
          // If button not found after delay, try again or show error
          setTimeout(() => {
            const retryButton = buttonContainer.querySelector('div[role="button"]');
            if (retryButton) {
              retryButton.click();
            } else {
              toast.error('Google Sign-In button failed to load. Please refresh the page or use email signin.', {
                toastId: 'google-button-error',
                autoClose: 5000,
              });
              // Cleanup
              if (document.body.contains(buttonContainer)) {
                document.body.removeChild(buttonContainer);
              }
            }
          }, 500);
        }

        // Cleanup after a delay
        setTimeout(() => {
          if (document.body.contains(buttonContainer)) {
            document.body.removeChild(buttonContainer);
          }
        }, 10000);
      }, 200);
    } catch (error) {
      console.error('Error triggering Google Sign-In:', error);

      // Provide helpful error message about FedCM
      const errorMessage = error?.message || error?.toString() || '';
      if (errorMessage.includes('FedCM') || errorMessage.includes('fedcm')) {
        toast.error(
          'FedCM (Federated Credential Management) is disabled in your browser. ' +
          'Please enable it in Site Settings or use email signin instead.',
          {
            toastId: 'google-fedcm-error',
            autoClose: 10000,
          }
        );
      } else {
        toast.error('Failed to open Google Sign-In. Please try again or use email signin.', {
          toastId: 'google-error',
        });
      }
    }
  };

  const handleAppleSignin = () => {
    toast.info('Apple sign in would be implemented with OAuth');
  };

  return (
    <div className="signin-page">
      <Navbar variant="transparent" />

      {loading && <LoadingSpinner fullScreen text="Signing you in..." />}

      <Container className="signin-container">
        <div className="signin-header">
          <h1>Welcome Back</h1>
          <p>Sign in to continue to your avatars</p>
        </div>

        <div className="signin-form-wrapper">
          <Form onSubmit={handleSubmit(onSubmit)} className="signin-form">
            {/* Email Field */}
            <div className="form-section">
              <label className="form-label-custom">Email Address <span className="required-asterisk">*</span></label>
              <Form.Control
                type="email"
                placeholder="you@example.com"
                className={`form-input ${errors.email ? 'is-invalid' : ''}`}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
              {errors.email && (
                <div className="error-message">{errors.email.message}</div>
              )}
            </div>

            {/* Password Field */}
            <div className="form-section">
              <label className="form-label-custom">Password <span className="required-asterisk">*</span></label>
              <div className="password-input-wrapper">
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  className={`form-input ${errors.password ? 'is-invalid' : ''}`}
                  {...register('password', {
                    required: 'Password is required'
                  })}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <div className="error-message">{errors.password.message}</div>
              )}
              <div className="forgot-password">
                <Link to="/forgot-password">Forgot password?</Link>
              </div>
            </div>

            <CustomButton
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              className="signin-submit"
            >
              Sign In
            </CustomButton>

            {/* Social Divider */}
            <div className="signin-divider">
              <span>or sign in with</span>
            </div>

            {/* Social Signin Buttons */}
            <div className="social-signin">
              <button
                type="button"
                className={`social-btn ${inviteId ? 'social-btn-disabled' : ''}`}
                onClick={inviteId ? undefined : handleGoogleSignin}
                disabled={inviteId}
                title={inviteId ? 'Social login is disabled for invite links. Please use email signin.' : undefined}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                Google
              </button>
              <button
                type="button"
                className={`social-btn ${inviteId ? 'social-btn-disabled' : ''}`}
                onClick={inviteId ? undefined : handleAppleSignin}
                disabled={inviteId}
                title={inviteId ? 'Social login is disabled for invite links. Please use email signin.' : undefined}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.45z" />
                </svg>
                Apple
              </button>
            </div>

            {/* Footer */}
            <div className="signin-footer">
              Don't have an account? <Link to="/signup">Sign up</Link>
            </div>
          </Form>
        </div>
      </Container>
    </div>
  );
};

export default SigninPage;
